import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { projectPointOntoPlane, sortPointsCounterClockwise } from '@editorUtils'
import { filter, inRange, isNull, reduce, toNumber } from 'lodash-es'
import { Plane, Vector3 } from 'three'
import {
  AddCircleOutline,
  ArrowDownward,
  ArrowUpward,
  ErrorOutline,
  RemoveCircleOutline,
  CloseFullscreen as CloseFullscreenIcon,
} from '@mui/icons-material'
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty'
import { Box, Divider, Stack, Typography, Button, TextField, InputAdornment } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { SwitchLabeled } from '@ui/actions'
import { TooltipIconButton } from '@ui/feedback'
import { CoordinatePointInput, NumberInput } from '@ui/forms'
import { createDefaultXYPointManipulationConfig } from '@ui/forms/CoordinatePointInput/pointManipulationConfig'
import { CollapsibleBoxInternalState } from '@ui/structure'
import { useEditElementStore } from '@editorStores'
import { useSceneMarker } from '@editorHooks'
import { rotateArray } from '@utils'
import { roofPlaneThreshold } from 'src/components/pages/Editor/config'
import XYZRotation from '../XYZRotation'

interface Props {
  selectedElement: string
  showMarker?: boolean
  zComputed: boolean
  setZComputed: (zComputed: boolean) => void
  showZComputedSwitch?: boolean
  showCalculateZFromOtherPoints?: boolean
  showPointRotation?: boolean
  showTranslationAndRotation?: boolean
  syncZOnChange?: boolean
  preserveZOnReplace?: boolean
}

const FormFields = ({
  selectedElement,
  showMarker = false,
  zComputed,
  setZComputed,
  showZComputedSwitch = true,
  showCalculateZFromOtherPoints = true,
  showPointRotation = true,
  showTranslationAndRotation = true,
  syncZOnChange = false,
  preserveZOnReplace = false,
}: Props): ReactElement => {
  const { setValue, formState } = useFormContext()

  const formPoints = useWatch({ name: 'points' }) as { x: number; y: number; z: number }[]
  const newPoint = useWatch({ name: 'newPoint' })

  const activeRoofPointIndex = useEditElementStore(state => state.activeRoofPointIndex)

  const sceneMarker = useMemo(() => {
    if (!isNull(activeRoofPointIndex) && showMarker) {
      const activePoint = formPoints[activeRoofPointIndex]
      if (activePoint) {
        return new ImmutableVector3(activePoint.x, activePoint.y, activePoint.z)
      }
    }
    return null
  }, [activeRoofPointIndex, formPoints, showMarker])

  useSceneMarker({
    marker: sceneMarker as unknown as ImmutableVector3,
    enabled: !!sceneMarker,
    disableDepthTest: true,
  })

  const formPointsAsVectors = useMemo(
    () => formPoints.map(p => new Vector3(p.x, p.y, p.z)),
    [formPoints],
  )

  const currentRoofPlane = useMemo(() => {
    const allPointsHaveSameZ = formPoints.every(p => p.z === formPoints[0].z)

    if (allPointsHaveSameZ) {
      const normal = new Vector3(0, 0, 1)
      const coplanarPoint = formPointsAsVectors[0]
      return new Plane().setFromNormalAndCoplanarPoint(normal, coplanarPoint)
    }

    return new Plane().setFromCoplanarPoints(
      formPointsAsVectors[0],
      formPointsAsVectors[1],
      formPointsAsVectors[2],
    )
  }, [formPoints, formPointsAsVectors])

  // in order to show errors immediately (and not only on submit of the form),
  // we use a memo here to get any points that are not coplanar to the roof
  // plane and display them below
  const errorMessages = useMemo(() => {
    return reduce(
      formPointsAsVectors,
      (collector, p, i) => {
        const distance = currentRoofPlane.distanceToPoint(p)
        if (!inRange(distance, -roofPlaneThreshold, roofPlaneThreshold)) {
          const message = `Punkt ${
            i + 1
          } ist nicht innerhalb der gleichen Fläche wie die ersten 3 Punkte des Daches`
          return [...collector, message]
        }

        return collector
      },
      [] as string[],
    )
  }, [formPointsAsVectors, currentRoofPlane])

  const removePoint = (indexToRemove: number) => {
    const newPoints = filter(formPoints, (_, i) => i !== indexToRemove)
    setValue('points', newPoints)
  }

  const addPoint = () => {
    const slabZ = formPoints[0]?.z ?? newPoint.z
    const pointToAdd = preserveZOnReplace ? { ...newPoint, z: slabZ } : newPoint
    const newPoints = [...formPoints, pointToAdd]
    const rotatedPoints = sortPointsCounterClockwise(newPoints)
    setValue('points', rotatedPoints)
  }

  const rotatePointsForward = () => {
    const newPoints = [...formPoints]
    const rotatedPoints = rotateArray(newPoints, 1)
    setValue('points', rotatedPoints)
  }
  const rotatePointsBackward = () => {
    const newPoints = [...formPoints]
    const rotatedPoints = rotateArray(newPoints, -1)
    setValue('points', rotatedPoints)
  }

  const shiftPointUp = (index: number) => {
    const newPoints = [...formPoints]
    const pointToMove = newPoints[index]

    // Remove the point from its current position
    newPoints.splice(index, 1)

    // Insert it at the previous position (cyclically)
    const newIndex = index === 0 ? newPoints.length : index - 1
    newPoints.splice(newIndex, 0, pointToMove)

    setValue('points', newPoints)
  }

  const shiftPointDown = (index: number) => {
    const newPoints = [...formPoints]
    const pointToMove = newPoints[index]

    // Remove the point from its current position
    newPoints.splice(index, 1)

    // Insert it at the next position (cyclically)
    const newIndex = index >= newPoints.length ? 0 : index + 1
    newPoints.splice(newIndex, 0, pointToMove)

    setValue('points', newPoints)
  }

  // XYZ Translation state and handlers
  const [xTranslation, setXTranslation] = useState<number>(0)
  const [yTranslation, setYTranslation] = useState<number>(0)
  const [zTranslation, setZTranslation] = useState<number>(0)

  const resetTranslation = useCallback(() => {
    setXTranslation(0)
    setYTranslation(0)
    setZTranslation(0)
  }, [])

  const handleApplyTranslation = useCallback(() => {
    const originalPoints = formState.defaultValues?.points as
      | { x: number; y: number; z: number }[]
      | undefined

    if (!originalPoints || originalPoints.length === 0) return

    // Apply translation to all points
    const translatedPoints = originalPoints.map(p => ({
      x: p.x + xTranslation,
      y: p.y + yTranslation,
      z: p.z + zTranslation,
    }))

    // Update the form values
    setValue('points', translatedPoints, { shouldDirty: false })
    resetTranslation()
  }, [
    xTranslation,
    yTranslation,
    zTranslation,
    setValue,
    formState.defaultValues,
    resetTranslation,
  ])

  const handleResetTranslation = useCallback(() => {
    resetTranslation()
    // Reset to original points from defaultValues
    if (formState.defaultValues?.points) {
      setValue('points', formState.defaultValues.points, { shouldDirty: false })
    }
  }, [resetTranslation, formState.defaultValues, setValue])

  const calculateZFromOtherPoints = (pointIndex: number) => {
    if (formPoints.length < 4) return

    // Get all points except the current one from form values
    const otherFormPoints = formPoints.filter((_, i) => i !== pointIndex)

    // Need at least 3 other points to define a plane
    if (otherFormPoints.length < 3) return

    // Take the first 3 other points and convert to ImmutableVector3
    const [p1, p2, p3] = otherFormPoints
      .slice(0, 3)
      .map(p => new ImmutableVector3(toNumber(p.x), toNumber(p.y), toNumber(p.z)))

    // Create a plane from these 3 points
    const plane = new Plane().setFromCoplanarPoints(p1.v, p2.v, p3.v)

    // Get current point's xy coordinates from form
    const currentPoint = formPoints[pointIndex]
    if (!currentPoint) return

    // Project the point with current xy onto the plane to get z
    const projectedPoint = projectPointOntoPlane(
      plane,
      new ImmutableVector3(toNumber(currentPoint.x), toNumber(currentPoint.y), 0),
    ) as ImmutableVector3

    // Update the z value
    setValue(`points[${pointIndex}].z`, projectedPoint.z)
  }

  return (
    <>
      <Stack spacing={1} data-cy="roof-form-fields">
        {formPoints.map((_, inputI) => {
          // Only render if the point exists in formPoints to prevent undefined errors
          const formPoint = formPoints[inputI]
          if (!formPoint) return null

          const actions = (
            <>
              <TooltipIconButton
                tooltip="Punkt nach oben verschieben"
                tooltipProps={{ placement: 'bottom' }}
                size="small"
                onClick={() => shiftPointUp(inputI)}
                sx={{ padding: 0 }}
              >
                <ArrowUpward fontSize="small" />
              </TooltipIconButton>

              <TooltipIconButton
                tooltip="Punkt nach unten verschieben"
                tooltipProps={{ placement: 'bottom' }}
                size="small"
                onClick={() => shiftPointDown(inputI)}
                sx={{ padding: 0 }}
              >
                <ArrowDownward fontSize="small" />
              </TooltipIconButton>
              {showCalculateZFromOtherPoints && (
                <TooltipIconButton
                  tooltip={
                    formPoints.length < 4
                      ? 'Z-Wert aus anderen Punkten berechnen (mindestens 4 Punkte erforderlich)'
                      : 'Z-Wert aus XY-Koordinaten und Ebene der ersten 3 anderen Punkte berechnen'
                  }
                  tooltipProps={{ placement: 'bottom' }}
                  onClick={() => calculateZFromOtherPoints(inputI)}
                  disabled={formPoints.length < 4}
                  size="small"
                  sx={{ padding: 0 }}
                >
                  <CloseFullscreenIcon fontSize="small" />
                </TooltipIconButton>
              )}
              <TooltipIconButton
                tooltip={
                  formPoints.length < 4
                    ? 'Weniger als 3 Punkte ist nicht möglich'
                    : 'Punkt entfernen'
                }
                tooltipProps={{ placement: 'bottom' }}
                onClick={() => removePoint(inputI)}
                disabled={formPoints.length < 4}
                size="small"
                sx={{ padding: 0 }}
              >
                <RemoveCircleOutline />
              </TooltipIconButton>
            </>
          )

          return (
            <Stack
              direction="column"
              key={`${selectedElement}-${formPoints.length}-${inputI}`}
              spacing={0}
            >
              <Stack direction="row" spacing={1}>
                <CoordinatePointInput
                  label={`P${inputI + 1}`}
                  name={`points[${inputI}]`}
                  pointManipulation={createDefaultXYPointManipulationConfig({
                    z: { disabled: zComputed, roundOnReplace: false },
                  })}
                  onChangeX={value => {
                    if (!zComputed || value === null) return

                    const p = formPoints[inputI]
                    const pProjected = projectPointOntoPlane(
                      currentRoofPlane,
                      new ImmutableVector3(toNumber(value), p.y, p.z),
                    )
                    setValue(`points[${inputI}].z`, pProjected.z)
                  }}
                  onChangeY={value => {
                    if (!zComputed || value === null) return

                    const p = formPoints[inputI]
                    const pProjected = projectPointOntoPlane(
                      currentRoofPlane,
                      new ImmutableVector3(p.x, toNumber(value), p.z),
                    )
                    setValue(`points[${inputI}].z`, pProjected.z)
                  }}
                  onChangeZ={value => {
                    if (!syncZOnChange || value === null) return

                    const oldZ = toNumber(formPoints[inputI].z)
                    const newZ = toNumber(value)
                    const delta = newZ - oldZ
                    if (delta === 0) return

                    const newPoints = formPoints.map(p => ({
                      ...p,
                      z: toNumber(p.z) + delta,
                    }))
                    setValue('points', newPoints)
                  }}
                  onReplace={
                    preserveZOnReplace
                      ? (x, y) => {
                          const currentZ = formPoints[inputI].z
                          setValue(`points[${inputI}]`, { x, y, z: currentZ })
                        }
                      : undefined
                  }
                  actions={actions}
                />
              </Stack>
            </Stack>
          )
        })}

        <Divider>{'Neuen Punkt hinzufügen'}</Divider>

        <CoordinatePointInput
          label={`P${formPoints.length + 1}`}
          name="newPoint"
          pointManipulation={createDefaultXYPointManipulationConfig()}
          onReplace={
            preserveZOnReplace
              ? (x, y) => {
                  const slabZ = formPoints[0]?.z ?? 0
                  setValue('newPoint', { x, y, z: slabZ })
                }
              : undefined
          }
          actions={
            <TooltipIconButton
              tooltip={formPoints.length < 4 ? 'Weniger als 3 Punkte ist nicht möglich' : ''}
              tooltipProps={{ placement: 'bottom' }}
              onClick={addPoint}
              size="small"
              sx={{ padding: 0 }}
            >
              <AddCircleOutline />
            </TooltipIconButton>
          }
        />

        {errorMessages.map(message => (
          <Box
            key={message}
            sx={({ spacing, palette }) => ({
              display: 'flex',
              padding: spacing(0.5),
              background: '#fff',
              color: palette.error.main,
              border: '1px solid',
              borderColor: palette.error.main,
              borderRadius: '4px',
            })}
          >
            <ErrorOutline fontSize="small" sx={{ marginRight: ({ spacing }) => spacing(0.5) }} />
            <Typography sx={{ fontSize: 12 }}>{message}</Typography>
          </Box>
        ))}

        {(showPointRotation || showZComputedSwitch) && (
          <>
            <Box pt={1}>
              <Divider />
            </Box>
            <Stack direction="row" spacing={1}>
              {showPointRotation && (
                <>
                  <TooltipIconButton
                    tooltip="Punkte vorwärts rotieren"
                    tooltipProps={{ placement: 'bottom' }}
                    size="small"
                    onClick={rotatePointsForward}
                  >
                    <ArrowUpward fontSize="small" />
                  </TooltipIconButton>
                  <TooltipIconButton
                    tooltip="Punkte rückwärts rotieren"
                    tooltipProps={{ placement: 'bottom' }}
                    size="small"
                    onClick={rotatePointsBackward}
                  >
                    <ArrowDownward fontSize="small" />
                  </TooltipIconButton>
                </>
              )}
              {showZComputedSwitch && (
                <SwitchLabeled
                  checked={zComputed}
                  onChange={() => setZComputed(!zComputed)}
                  label="Z Wert anhand Dachfläche berechnen"
                  data-cy="switch-z-computed"
                />
              )}
            </Stack>
          </>
        )}

        {showTranslationAndRotation && (
          <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
            <CollapsibleBoxInternalState
              variant="expander"
              label="verschieben / drehen (XYZ)"
              defaultExpanded={true}
              startIcon={<ThreeSixtyIcon sx={{ color: 'grey.600', padding: '2px' }} />}
            >
              <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  sx={{ width: '100%' }}
                >
                  <Stack direction="row" spacing={1} alignItems={'center'}>
                    <TextField
                      variant="outlined"
                      label="X"
                      size="small"
                      placeholder="0,00"
                      value={xTranslation || ''}
                      onChange={event => {
                        const value = event.target.value.length
                          ? parseFloat(event.target.value) || 0
                          : 0
                        setXTranslation(value)
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        inputComponent: NumberInput as any,
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems={'center'}>
                    <TextField
                      variant="outlined"
                      label="Y"
                      size="small"
                      placeholder="0,00"
                      value={yTranslation || ''}
                      onChange={event => {
                        const value = event.target.value.length
                          ? parseFloat(event.target.value) || 0
                          : 0
                        setYTranslation(value)
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        inputComponent: NumberInput as any,
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems={'center'}>
                    <TextField
                      label="Z"
                      variant="outlined"
                      size="small"
                      placeholder="0,00"
                      value={zTranslation || ''}
                      onChange={event => {
                        const value = event.target.value.length
                          ? parseFloat(event.target.value) || 0
                          : 0
                        setZTranslation(value)
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        inputComponent: NumberInput as any,
                      }}
                    />
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyTranslation}
                    disabled={xTranslation === 0 && yTranslation === 0 && zTranslation === 0}
                    fullWidth
                  >
                    Anwenden
                  </Button>
                  <Button
                    size="small"
                    onClick={handleResetTranslation}
                    fullWidth
                    variant="outlined"
                  >
                    Zurücksetzen
                  </Button>
                </Stack>
                <XYZRotation />
              </Stack>
            </CollapsibleBoxInternalState>
          </Stack>
        )}
      </Stack>
    </>
  )
}

export default FormFields
